// Forked from https://github.com/dai-shi/use-context-selector/blob/2dd334d727fc3b4cbadf7876b6ce64e0c633fd25/src/index.ts
// Changes: removed scheduler dependency, unused functions and added equalityFn support
import { createElement, createContext as createContextOrig, useContext as useContextOrig, useEffect, useLayoutEffect, useReducer, useRef, } from 'react';
const CONTEXT_VALUE = Symbol();
const ORIGINAL_PROVIDER = Symbol();
const isSSR = typeof window === 'undefined';
const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect;
// for preact that doesn't have runWithPriority
const runWithNormalPriority = (thunk) => thunk();
const createProvider = (ProviderOrig) => ({ value, children }) => {
    const valueRef = useRef(value);
    const versionRef = useRef(0);
    const contextValue = useRef();
    if (!contextValue.current) {
        const listeners = new Set();
        const update = (thunk) => {
            versionRef.current += 1;
            listeners.forEach((listener) => listener([versionRef.current]));
            thunk();
        };
        contextValue.current = {
            [CONTEXT_VALUE]: {
                /* "v"alue     */ v: valueRef,
                /* versio"n"   */ n: versionRef,
                /* "l"isteners */ l: listeners,
                /* "u"pdate    */ u: update,
            },
        };
    }
    useIsomorphicLayoutEffect(() => {
        valueRef.current = value;
        versionRef.current += 1;
        runWithNormalPriority(() => {
            contextValue.current[CONTEXT_VALUE].l.forEach((listener) => {
                listener([versionRef.current, value]);
            });
        });
    }, [value]);
    return createElement(ProviderOrig, { value: contextValue.current }, children);
};
const identity = (x) => x;
/**
 * This creates a special context for `useContextSelector`.
 *
 * @example
 * import { createContext } from 'use-context-selector';
 *
 * const PersonContext = createContext({ firstName: '', familyName: '' });
 */
export function createContext(defaultValue) {
    const context = createContextOrig({
        [CONTEXT_VALUE]: {
            /* "v"alue     */ v: { current: defaultValue },
            /* versio"n"   */ n: { current: -1 },
            /* "l"isteners */ l: new Set(),
            /* "u"pdate    */ u: (f) => f(),
        },
    });
    context[ORIGINAL_PROVIDER] = context.Provider;
    context.Provider = createProvider(context.Provider);
    delete context.Consumer; // no support for Consumer
    return context;
}
/**
 * This hook returns context selected value by selector.
 *
 * It will only accept context created by `createContext`.
 * It will trigger re-render if only the selected value is referentially changed.
 *
 * The selector should return referentially equal result for same input for better performance.
 *
 * @example
 * import { useContextSelector } from 'use-context-selector';
 *
 * const firstName = useContextSelector(PersonContext, state => state.firstName);
 */
export function useContextSelector(context, selector, equalityFn) {
    const contextValue = useContextOrig(context)[CONTEXT_VALUE];
    if (typeof process === 'object' && process.env.NODE_ENV !== 'production') {
        if (!contextValue) {
            throw new Error('useContextSelector requires special context');
        }
    }
    const { 
    /* "v"alue     */ v: { current: value }, 
    /* versio"n"   */ n: { current: version }, 
    /* "l"isteners */ l: listeners, } = contextValue;
    const selected = selector(value);
    const [state, dispatch] = useReducer((prev, next) => {
        if (!next) {
            return [value, selected];
        }
        if (next[0] <= version) {
            if (equalityFn(prev[1], selected)) {
                return prev; // bail out
            }
            return [value, selected];
        }
        try {
            if (next.length === 2) {
                if (equalityFn(prev[0], next[1])) {
                    return prev; // do not update
                }
                const nextSelected = selector(next[1]);
                if (equalityFn(prev[1], nextSelected)) {
                    return prev; // do not update
                }
                return [next[1], nextSelected];
            }
        }
        catch (e) {
            // ignored (stale props or some other reason)
        }
        return [...prev]; // schedule update
    }, [value, selected]);
    if (!equalityFn(state[1], selected)) {
        // schedule re-render
        // this is safe because it's self contained
        dispatch();
    }
    useIsomorphicLayoutEffect(() => {
        listeners.add(dispatch);
        return () => {
            listeners.delete(dispatch);
        };
    }, [listeners]);
    return state[1];
}
/**
 * This hook returns the entire context value.
 * Use this instead of React.useContext for consistent behavior.
 *
 * @example
 * import { useContext } from 'use-context-selector';
 *
 * const person = useContext(PersonContext);
 */
export function useContext(context) {
    return useContextSelector(context, identity, Object.is);
}
/**
 * This hook returns an update function that accepts a thunk function
 *
 * Use this for a function that will change a value in
 * [Concurrent Mode](https://reactjs.org/docs/concurrent-mode-intro.html).
 * Otherwise, there's no need to use this hook.
 *
 * @example
 * import { useContextUpdate } from 'use-context-selector';
 *
 * const update = useContextUpdate();
 * update(() => setState(...));
 */
// Not needed ATM
// export function useContextUpdate<Value>(context: Context<Value>) {
//   const contextValue = useContextOrig((context as unknown) as ContextOrig<ContextValue<Value>>)[
//     CONTEXT_VALUE
//   ];
//   if (typeof process === 'object' && process.env.NODE_ENV !== 'production') {
//     if (!contextValue) {
//       throw new Error('useContextUpdate requires special context');
//     }
//   }
//   const { u: update } = contextValue;
//   return update;
// }
//# sourceMappingURL=use-context-selector.js.map